import React from 'react'
import { useForm } from 'react-hook-form'
import { Modal, Input } from '../../../shared/components'
import { useToast } from '../../../contexts/ToastContext'
import { userService } from '../../../services'
import { colors, typography, spacing, borderRadius, shadows } from '../../../styles/design-system'
interface AccountSecurityTabProps {
  isMobile?: boolean
}

interface ChangePasswordFormData {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const AccountSecurityTab: React.FC<AccountSecurityTabProps> = ({ isMobile = false }) => {
  const { showSuccess, showError } = useToast()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [generalError, setGeneralError] = React.useState('')

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<ChangePasswordFormData>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  })

  const watchedNewPassword = watch('newPassword')

  const labelStyle = React.useMemo<React.CSSProperties>(() => ({
    display: 'block',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.neutral.gray700,
    marginBottom: spacing.xs
  }), [])

  const inputStyle = React.useMemo<React.CSSProperties>(() => ({
    width: '100%',
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.neutral.gray200}`,
    backgroundColor: colors.neutral.white,
    fontSize: typography.fontSize.base,
    color: colors.neutral.gray900,
    outline: 'none',
    transition: 'all 0.2s ease',
    boxShadow: 'none'
  }), [])

  const errorTextStyle = React.useMemo<React.CSSProperties>(() => ({
    color: colors.status.error,
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs,
    marginBottom: 0
  }), [])

  const closeModal = React.useCallback(() => {
    setIsModalOpen(false)
    reset()
    setGeneralError('')
  }, [reset])

  const onSubmit = React.useCallback(async (data: ChangePasswordFormData) => {
    setGeneralError('')

    try {
      setIsSubmitting(true)
      const payload = {
        currentPassword: data.currentPassword.trim(),
        newPassword: data.newPassword.trim()
      }
      const response = await userService.changePassword(payload)
      showSuccess('Senha atualizada', response.message)
      closeModal()
    } catch (error) {
      const message =
        (typeof error === 'object' && error !== null && 'message' in error)
          ? String((error as { message?: string }).message)
          : 'Não foi possível alterar sua senha. Tente novamente.'
      setGeneralError(message)
      showError('Erro ao alterar senha', message)
    } finally {
      setIsSubmitting(false)
    }
  }, [closeModal, showError, showSuccess])

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: borderRadius.xl,
        boxShadow: shadows.sm,
        padding: isMobile ? spacing.lg : spacing['2xl']
      }}
    >
      <h2
        style={{
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.neutral.gray900,
          margin: 0,
          marginBottom: spacing.xl
        }}
      >
        Segurança
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
        <div
          style={{
            border: `1px solid ${colors.neutral.gray200}`,
            borderRadius: borderRadius.xl,
            padding: spacing.xl,
            backgroundColor: colors.neutral.gray50
          }}
        >
          <h3
            style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              color: colors.neutral.gray900,
              margin: 0,
              marginBottom: spacing.sm
            }}
          >
            Alterar Senha
          </h3>
          <p
            style={{
              color: colors.neutral.gray600,
              margin: 0,
              marginBottom: spacing.lg,
              fontSize: typography.fontSize.sm
            }}
          >
            Para sua segurança, recomendamos alterar sua senha regularmente.
          </p>
          <button
            type="button"
            style={{
              backgroundColor: colors.primary.blue,
              color: colors.neutral.white,
              border: 'none',
              padding: `${spacing.md} ${spacing.lg}`,
              borderRadius: borderRadius.lg,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: shadows.sm,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm
            }}
            onClick={() => setIsModalOpen(true)}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
              event.currentTarget.style.transform = 'translateY(-2px)'
              event.currentTarget.style.boxShadow = shadows.md
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = colors.primary.blue
              event.currentTarget.style.transform = 'translateY(0)'
              event.currentTarget.style.boxShadow = shadows.sm
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Alterar Senha
          </button>
        </div>

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title="Alterar Senha"
          size="sm"
          footer={
            <>
              <button
                type="button"
                style={{
                  border: `1px solid ${colors.neutral.gray300}`,
                  backgroundColor: colors.neutral.white,
                  color: colors.neutral.gray700,
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: borderRadius.lg,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={closeModal}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colors.neutral.gray50
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colors.neutral.white
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="change-password-form"
                style={{
                  backgroundColor: colors.primary.blue,
                  color: colors.neutral.white,
                  border: 'none',
                  padding: `${spacing.sm} ${spacing.lg}`,
                  borderRadius: borderRadius.lg,
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: isSubmitting ? 'wait' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: shadows.sm,
                  opacity: isSubmitting ? 0.7 : 1
                }}
                disabled={isSubmitting}
                onMouseEnter={(event) => {
                  if (!isSubmitting) {
                    event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
                    event.currentTarget.style.transform = 'translateY(-2px)'
                    event.currentTarget.style.boxShadow = shadows.md
                  }
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colors.primary.blue
                  event.currentTarget.style.transform = 'translateY(0)'
                  event.currentTarget.style.boxShadow = shadows.sm
                }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar nova senha'}
              </button>
            </>
          }
        >
          <form id="change-password-form" onSubmit={handleSubmit(onSubmit)} aria-describedby={generalError ? 'change-password-error' : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div>
                <label style={labelStyle} htmlFor="current-password">
                  Senha atual
                </label>
                <Input
                  id="current-password"
                  type="password"
                  {...register('currentPassword', {
                    required: 'Informe sua senha atual.'
                  })}
                  style={{
                    ...inputStyle,
                    borderColor: errors.currentPassword ? colors.status.error : colors.neutral.gray200
                  }}
                  onFocus={(event) => (event.currentTarget.style.borderColor = colors.primary.blue)}
                  onBlur={(event) => (event.currentTarget.style.borderColor = errors.currentPassword ? colors.status.error : colors.neutral.gray200)}
                  autoComplete="current-password"
                  aria-required="true"
                  aria-invalid={errors.currentPassword ? 'true' : 'false'}
                  aria-describedby={errors.currentPassword ? 'current-password-error' : undefined}
                />
                {errors.currentPassword && (
                  <p id="current-password-error" style={errorTextStyle} role="alert">
                    {errors.currentPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle} htmlFor="new-password">
                  Nova senha
                </label>
                <Input
                  id="new-password"
                  type="password"
                  {...register('newPassword', {
                    required: 'Informe a nova senha.',
                    minLength: { value: 6, message: 'A nova senha deve ter pelo menos 6 caracteres.' }
                  })}
                  style={{
                    ...inputStyle,
                    borderColor: errors.newPassword ? colors.status.error : colors.neutral.gray200
                  }}
                  onFocus={(event) => (event.currentTarget.style.borderColor = colors.primary.blue)}
                  onBlur={(event) => (event.currentTarget.style.borderColor = errors.newPassword ? colors.status.error : colors.neutral.gray200)}
                  autoComplete="new-password"
                  aria-required="true"
                  aria-invalid={errors.newPassword ? 'true' : 'false'}
                  aria-describedby={errors.newPassword ? 'new-password-error' : 'new-password-helper'}
                />
                <p id="new-password-helper" style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray500, marginTop: spacing.xs, marginBottom: 0 }}>
                  Use pelo menos 6 caracteres e combine letras, números ou símbolos.
                </p>
                {errors.newPassword && (
                  <p id="new-password-error" style={errorTextStyle} role="alert">
                    {errors.newPassword.message}
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle} htmlFor="confirm-password">
                  Confirmar nova senha
                </label>
                <Input
                  id="confirm-password"
                  type="password"
                  {...register('confirmPassword', {
                    required: 'Confirme a nova senha.',
                    validate: (value) => value === watchedNewPassword || 'A confirmação deve ser igual à nova senha.'
                  })}
                  style={{
                    ...inputStyle,
                    borderColor: errors.confirmPassword ? colors.status.error : colors.neutral.gray200
                  }}
                  onFocus={(event) => (event.currentTarget.style.borderColor = colors.primary.blue)}
                  onBlur={(event) => (event.currentTarget.style.borderColor = errors.confirmPassword ? colors.status.error : colors.neutral.gray200)}
                  autoComplete="new-password"
                  aria-required="true"
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                />
                {errors.confirmPassword && (
                  <p id="confirm-password-error" style={errorTextStyle} role="alert">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
            </div>

            {generalError && (
              <p id="change-password-error" style={{ ...errorTextStyle, marginTop: spacing.lg }} role="alert" aria-live="assertive">
                {generalError}
              </p>
            )}
          </form>
        </Modal>

      </div>
    </div>
  )
}

export default AccountSecurityTab

